import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import { toast } from 'react-toastify';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const COLORS = ['#fbbf24', '#10b981', '#3b82f6']; // Yellow (Escrow), Green (Workers), Blue (Revenue)

function AdminRevenue() {
    const [ledger, setLedger] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const userEmail = localStorage.getItem('email');

    useEffect(() => {
        if (!token || role !== 'ADMIN') {
            navigate('/admin-login');
            return;
        }
        fetchLedger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchLedger = async () => {
        setLoading(true);
        try {
            // Dev 2's new endpoint
            const res = await axios.get(`${API_URL}/api/admin/ledger`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLedger(res.data);
        } catch (err) {
            toast.error('Failed to load ledger data');
        }
        setLoading(false);
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
    };

    if (loading) {
        return (
            <div className="dashboard-layout" style={{ alignItems: 'center', justifyContent: 'center' }}>
                <div className="spinner" style={{ width: '24px', height: '24px', borderTopColor: '#fff' }}></div>
            </div>
        );
    }

    if (!ledger) return null;

    // Prepare data for the charts
    const pieData = [
        { name: 'Locked Escrow', value: ledger.totalEscrow || 0 },
        { name: 'Worker Balances', value: ledger.totalWorkerBalances || 0 },
        { name: 'Platform Revenue', value: ledger.totalPlatformRevenue || 0 },
    ];

    // Format ledger entries for the bar chart
    const barData = (ledger.recentLedgerEntries || []).slice(0, 10).reverse().map(entry => ({
        name: entry.description || entry.type,
        amount: entry.amount
    }));

    return (
        <div className="dashboard-layout">
            <aside className="sidebar" style={{ borderRight: '1px solid var(--error)' }}>
                <Link to="/" className="sidebar-brand">infloir <span style={{ color: 'var(--error)', fontSize: '12px' }}>ADMIN</span></Link>
                <nav className="sidebar-nav">
                    <Link to="/admin-dashboard" className="nav-item">
                        <span>🛡️</span> Overview
                    </Link>
                    <Link to="/admin/payouts" className="nav-item">
                        <span>💸</span> Manage Payouts
                    </Link>
                    <Link to="/admin/revenue" className={`nav-item ${location.pathname === '/admin/revenue' ? 'active' : ''}`}>
                        <span>💰</span> Revenue
                    </Link>
                </nav>
                <div style={{ marginTop: 'auto' }}>
                    <button onClick={handleLogout} className="nav-item" style={{ width: '100%', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer' }}>
                        <span>🚪</span> Log out
                    </button>
                </div>
            </aside>

            <div className="main-content">
                <header className="topbar">
                    <h2 style={{ fontSize: '18px', fontWeight: '500' }}>Platform Ledger</h2>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{userEmail}</span>
                </header>

                <main className="page-container page-fade-in">
                    
                    {/* TOP STATS */}
                    <div className="stats-grid">
                        <div className="card stat-card">
                            <p className="stat-label">Locked in Escrow</p>
                            <h3 className="stat-value" style={{ color: 'var(--warning)' }}>
                                ${parseFloat(ledger.totalEscrow || 0).toFixed(2)}
                            </h3>
                        </div>
                        <div className="card stat-card">
                            <p className="stat-label">Worker Balances</p>
                            <h3 className="stat-value" style={{ color: 'var(--success)' }}>
                                ${parseFloat(ledger.totalWorkerBalances || 0).toFixed(2)}
                            </h3>
                        </div>
                        <div className="card stat-card">
                            <p className="stat-label">Platform Revenue</p>
                            <h3 className="stat-value" style={{ color: 'var(--info)' }}>
                                ${parseFloat(ledger.totalPlatformRevenue || 0).toFixed(2)}
                            </h3>
                        </div>
                    </div>

                    {/* CHARTS GRID */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                        
                        {/* PIE CHART */}
                        <div className="card" style={{ padding: '24px' }}>
                            <h3 style={{ fontSize: '16px', marginBottom: '24px', color: 'var(--text)' }}>Capital Distribution</h3>
                            <div style={{ height: '300px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={100}
                                            paddingAngle={5}
                                            dataKey="value"
                                            stroke="none"
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            contentStyle={{ backgroundColor: '#111', borderColor: '#333', borderRadius: '8px', color: '#fff' }}
                                            formatter={(value) => `$${parseFloat(value).toFixed(2)}`}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '16px' }}>
                                {pieData.map((entry, index) => (
                                    <div key={entry.name} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: COLORS[index] }}></div>
                                        {entry.name}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* BAR CHART */}
                        <div className="card" style={{ padding: '24px' }}>
                            <h3 style={{ fontSize: '16px', marginBottom: '24px', color: 'var(--text)' }}>Recent Money Flow</h3>
                            <div style={{ height: '300px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                                        <XAxis dataKey="name" stroke="#666" tick={{ fill: '#666', fontSize: 12 }} />
                                        <YAxis stroke="#666" tick={{ fill: '#666', fontSize: 12 }} />
                                        <Tooltip 
                                            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                                            contentStyle={{ backgroundColor: '#111', borderColor: '#333', borderRadius: '8px', color: '#fff' }}
                                            formatter={(value) => `$${parseFloat(value).toFixed(2)}`}
                                        />
                                        <Bar dataKey="amount" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                </main>
            </div>
        </div>
    );
}

export default AdminRevenue;